import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Sun, Moon, Sparkles, ExternalLink, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTheme } from '../../context/ThemeContext';
import { useSubscription } from '../../hooks/useSubscription';
import { TIER_LABEL } from '../../config/tiers';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { UPGRADE_CTA } from '../../config/limits';
import { usePortalSession } from '../../hooks/usePortalSession';
import { CURRENCIES } from '../../types';
import { Footer } from '../Layout/Footer';
import { clearAllLocalAppData } from '../../lib/cloud/localCache';

/**
 * Account & preferences settings (chunk 1). Auth-guarded via <RequireAuth> in the
 * router. Reuses the existing CurrencyContext / ThemeContext (which already persist
 * to localStorage AND cloud-sync via the write-through) and supabase.auth.updateUser
 * for the password change. Subscription + Account management are placeholders for
 * chunk 2.
 *
 * Slate + bronze, theme-aware, responsive (single column, fits mobile).
 */

type Feedback = { type: 'success' | 'error'; text: string } | null;

const INPUT =
  'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-[var(--color-bronze-bright)] transition-colors';
const LABEL = 'block text-xs font-medium text-slate-400 mb-1.5';
const PRIMARY_BTN =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-[filter,opacity] hover:brightness-[1.06] disabled:opacity-60 disabled:hover:brightness-100';
const PRIMARY_STYLE: React.CSSProperties = { backgroundColor: 'var(--color-bronze-bright)', color: '#0c0e12' };
// Neutral action button (Manage subscription / Delete account) — calm, no red.
const SECONDARY_BTN =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium bg-slate-800 border border-slate-700 text-slate-100 hover:bg-slate-700 transition-colors disabled:opacity-60';

function FeedbackNote({ msg }: { msg: Feedback }) {
  if (!msg) return null;
  const ok = msg.type === 'success';
  return (
    <div
      className={`text-xs rounded-lg px-3 py-2 border ${
        ok
          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
          : 'bg-red-500/10 border-red-500/30 text-red-400'
      }`}
      role="status"
      aria-live="polite"
    >
      {msg.text}
    </div>
  );
}

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-bronze)' }}>
        {title}
      </h2>
      {desc && <p className="text-xs text-slate-500 mt-1">{desc}</p>}
      <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/40 p-5">{children}</div>
    </section>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { currency, setCurrencyCode } = useCurrency();
  const { theme, toggle } = useTheme();
  const { tier, isSubscribed, currentPeriodEnd, cancelAtPeriodEnd, loading: subLoading } = useSubscription();
  const { open: openUpgradePrompt } = useUpgradePrompt();
  const { openPortal, loading: portalLoading, error: portalError } = usePortalSession();

  // ── Change password ───────────────────────────────────────────────────────
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState<Feedback>(null);

  // ── Delete account ────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const setThemeTo = (t: 'light' | 'dark') => { if (t !== theme) toggle(); };

  const renewal = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  const handleDelete = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setDeleteError('Please sign in again.'); setDeleting(false); return; }

      const res = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let message = 'Could not delete your account. Please try again.';
        try { const body = await res.json(); if (body && typeof body.error === 'string') message = body.error; } catch { /* keep default */ }
        setDeleteError(message);
        setDeleting(false);
        return;
      }

      // Deleted server-side (cascade). Wipe this device's data, sign out (best
      // effort — the session is already invalid), and go home. A signOut hiccup
      // must not surface a misleading "couldn't delete" error or block the redirect.
      clearAllLocalAppData();
      try { await signOut(); } catch { /* session already gone */ }
      navigate('/', { replace: true });
    } catch {
      setDeleteError('Could not delete your account. Please check your connection and try again.');
      setDeleting(false);
    }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (pw1.length < 6) {
      setPwMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    if (pw1 !== pw2) {
      setPwMsg({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setPwLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) { setPwMsg({ type: 'error', text: error.message }); return; }
      setPwMsg({ type: 'success', text: 'Password updated.' });
      setPw1('');
      setPw2('');
    } catch {
      setPwMsg({ type: 'error', text: 'Something went wrong. Please try again.' });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* ── Top bar ── */}
      <div className="border-b border-slate-700 bg-slate-950/95 backdrop-blur-sm sticky top-0 z-30 px-4 sm:px-8 h-16 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/app')}
          className="flex items-center gap-1.5 text-slate-400 hover:text-slate-100 transition-colors text-sm font-medium flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        <h1 className="text-base font-bold text-slate-100 truncate">Settings</h1>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 space-y-8">

        {/* ── Account ── */}
        <Section title="Account" desc="Your login details.">
          <div className="space-y-6">

            {/* Email (read-only) */}
            <div>
              <p className={LABEL}>Email</p>
              <div className="text-sm text-slate-200 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2.5">
                <span className="truncate">{user?.email ?? '—'}</span>
              </div>
            </div>

            <div className="h-px bg-slate-700/40" />

            {/* Change password */}
            <form onSubmit={submitPassword} className="space-y-3">
              <p className="text-sm font-semibold text-slate-100">Change password</p>
              <div>
                <label className={LABEL} htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  placeholder="At least 6 characters"
                  className={INPUT}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="confirm-password">Confirm new password</label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Re-enter the password"
                  className={INPUT}
                />
              </div>
              <FeedbackNote msg={pwMsg} />
              <button type="submit" disabled={pwLoading} className={PRIMARY_BTN} style={PRIMARY_STYLE}>
                {pwLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Update password
              </button>
            </form>
          </div>
        </Section>

        {/* ── Preferences ── */}
        <Section title="Preferences" desc="Synced to your account and this device.">
          <div className="space-y-5">
            {/* Currency */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-medium text-slate-100">Display currency</p>
                <p className="text-xs text-slate-500 mt-0.5">Used across the app for all values.</p>
              </div>
              <select
                aria-label="Display currency"
                value={currency.code}
                onChange={(e) => setCurrencyCode(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-slate-500 cursor-pointer [color-scheme:dark]"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>

            <div className="h-px bg-slate-700/40" />

            {/* Theme */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-medium text-slate-100">Theme</p>
                <p className="text-xs text-slate-500 mt-0.5">Light or dark appearance.</p>
              </div>
              <div className="inline-flex bg-slate-800 border border-slate-700 rounded-lg p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setThemeTo('light')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    theme === 'light' ? 'bg-slate-700 text-slate-100' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Sun className="w-3.5 h-3.5" /> Light
                </button>
                <button
                  type="button"
                  onClick={() => setThemeTo('dark')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    theme === 'dark' ? 'bg-slate-700 text-slate-100' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Moon className="w-3.5 h-3.5" /> Dark
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* ── Subscription ── */}
        <Section title="Subscription" desc="Your plan and billing.">
          {subLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-slate-100">Current plan</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {isSubscribed
                      ? cancelAtPeriodEnd
                        ? renewal ? `Ends ${renewal}` : 'Subscription ending'
                        : renewal ? `Renews ${renewal}` : 'Active subscription'
                      : 'Upgrade to unlock Pro features.'}
                  </p>
                </div>
                <span
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full border"
                  style={
                    isSubscribed
                      ? { color: '#b89968', borderColor: 'rgba(212,180,131,0.32)', background: 'rgba(212,180,131,0.1)' }
                      : { color: 'rgb(var(--sl-300))', borderColor: 'rgb(var(--sl-700))', background: 'rgb(var(--sl-800))' }
                  }
                >
                  {TIER_LABEL[tier]}
                </span>
              </div>

              {isSubscribed ? (
                <>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {cancelAtPeriodEnd
                      ? 'Your subscription is set to end. Reactivate it, update your payment method, or view invoices in Stripe’s secure billing portal.'
                      : 'Cancel your subscription, update your payment method, or view invoices in Stripe’s secure billing portal.'}
                  </p>
                  <button type="button" onClick={openPortal} disabled={portalLoading} className={SECONDARY_BTN}>
                    {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    Manage subscription
                  </button>
                  {portalError && <FeedbackNote msg={{ type: 'error', text: portalError }} />}
                </>
              ) : (
                <button type="button" onClick={() => openUpgradePrompt(UPGRADE_CTA)} className={PRIMARY_BTN} style={PRIMARY_STYLE}>
                  <Sparkles className="w-4 h-4" />
                  Upgrade
                </button>
              )}
            </div>
          )}
        </Section>

        {/* ── Account management ── */}
        <Section title="Account management" desc="Manage your account and data.">
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-100">Delete account</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Permanently deletes your account and all your data, including transactions, plans, tax estimates, and preferences,
              and cancels any active subscription. This can’t be undone.
            </p>
            <button
              type="button"
              onClick={() => { setConfirmText(''); setDeleteError(null); setConfirmOpen(true); }}
              className={SECONDARY_BTN}
            >
              Delete account
            </button>
          </div>
        </Section>

      </div>

      {/* ── Delete confirmation modal (explicit "type DELETE" step) ── */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-bold text-slate-100">Delete your account?</h3>
              <button
                type="button"
                onClick={() => { if (!deleting) setConfirmOpen(false); }}
                aria-label="Close"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-slate-400 leading-relaxed">
              This permanently deletes your account and all your data, and cancels any active subscription.
              This action can’t be undone.
            </p>

            <div>
              <label className={LABEL} htmlFor="confirm-delete">
                Type <span className="font-semibold text-slate-200">DELETE</span> to confirm
              </label>
              <input
                id="confirm-delete"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className={INPUT}
              />
            </div>

            {deleteError && <FeedbackNote msg={{ type: 'error', text: deleteError }} />}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || confirmText.trim().toUpperCase() !== 'DELETE'}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium bg-slate-700 border border-slate-600 text-slate-100 hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:hover:bg-slate-700"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
